import React, { useState } from 'react';
import { 
  X, Video, Lock, Users, Eye, Globe, Hash, Leaf, 
  Clock, Star, Settings, Sparkles, Crown, Shield,
  Camera, Mic, Monitor, MessageCircle, Coffee, Zap,
  Plus, ChevronRight, ChevronLeft, AlertCircle
} from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
import API_URL from '../config/ApiUrl';
const CreateSeshRoomModal = ({ isOpen, onClose, onCreateRoom }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    strain: '',
    tags: [],
    is_public: false,
    max_participants: 8,
    max_viewers: 100,
    invite_only: false,
    auto_start_stream: true
  });
  
  const [currentTag, setCurrentTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { colors } = useTheme();
  
  const [activeStep, setActiveStep] = useState(1);

  // Cannabis community-focused categories
  const seshCategories = [
    { 
      id: 'wake-bake', 
      name: 'Wake & Bake', 
      icon: '🌅', 
      description: 'Morning sessions to start the day',
      color: '#f59e0b'
    },
    { 
      id: 'chill-sesh', 
      name: 'Chill Sesh', 
      icon: '😌', 
      description: 'Relaxed smoking sessions',
      color: '#10b981'
    },
    { 
      id: 'strain-review', 
      name: 'Strain Reviews', 
      icon: '🔍', 
      description: 'Test and review new strains together',
      color: '#8b5cf6'
    },
    { 
      id: 'dab-station', 
      name: 'Dab Station', 
      icon: '🔥', 
      description: 'Concentrate and dab sessions',
      color: '#ef4444'
    },
    { 
      id: 'edible-experience', 
      name: 'Edible Journey', 
      icon: '🍪', 
      description: 'Edible experiences and dosing',
      color: '#f97316'
    },
    { 
      id: 'grow-talk', 
      name: 'Grow Talk', 
      icon: '🌱', 
      description: 'Cultivation discussions and tips',
      color: '#22c55e'
    },
    { 
      id: 'gaming-high', 
      name: 'Gaming High', 
      icon: '🎮', 
      description: 'Gaming while elevated',
      color: '#3b82f6'
    },
    { 
      id: 'music-vibes', 
      name: 'Music & Vibes', 
      icon: '🎵', 
      description: 'Music listening sessions',
      color: '#ec4899'
    },
    { 
      id: 'newbie-friendly', 
      name: 'Newbie Corner', 
      icon: '🤝', 
      description: 'Beginner-friendly sessions',
      color: '#06b6d4'
    },
    { 
      id: 'private-crew', 
      name: 'Private Crew', 
      icon: '🔒', 
      description: 'Invite-only friend groups',
      color: '#6b7280'
    }
  ];

    const [createdRoom, setCreatedRoom] = useState(null);


  // Popular strains for suggestions
  const popularStrains = [
    'Blue Dream', 'OG Kush', 'Girl Scout Cookies', 'White Widow', 'Sour Diesel',
    'Granddaddy Purple', 'Green Crack', 'Jack Herer', 'Pineapple Express', 'AK-47',
    'Northern Lights', 'Purple Haze', 'Trainwreck', 'Bubba Kush', 'Lemon Haze',
    'Gorilla Glue', 'Gelato', 'Wedding Cake', 'Zkittlez', 'Runtz'
  ];

  // Community tags
  const communityTags = [
    // Strain types
    'sativa', 'indica', 'hybrid',
    // Vibes
    'chill', 'energetic', 'creative', 'social', 'relaxing', 'uplifting', 'giggly',
    // Session types
    'micro-dose', 'heavy-hitter', 'first-timer', 'veteran', 'medical', 'recreational',
    // Activities
    'gaming', 'music', 'art', 'cooking', 'outdoor', 'indoor', 'movie-night',
    // Time
    'morning', 'afternoon', 'evening', 'late-night', 'weekend', 'daily'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Room title is required';
    } else if (formData.title.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    } else if (formData.title.length > 80) {
      newErrors.title = 'Title must be less than 80 characters';
    }
    
    if (!formData.category) {
      newErrors.category = 'Please select a sesh category';
    }
    
    if (formData.description && formData.description.length > 300) {
      newErrors.description = 'Description must be less than 300 characters';
    }
    
    if (formData.max_participants < 2 || formData.max_participants > 20) {
      newErrors.max_participants = 'Participants must be between 2 and 20';
    }
    
    if (!formData.is_public && formData.max_viewers > 0) {
      newErrors.max_viewers = 'Private rooms cannot have viewers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addTag = (tag = null) => {
    const tagToAdd = tag || currentTag.trim().toLowerCase();
    if (tagToAdd && !formData.tags.includes(tagToAdd) && formData.tags.length < 8) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagToAdd]
      }));
      if (!tag) setCurrentTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const [stepChangeTime, setStepChangeTime] = useState(0);


  const handleSubmit = async (e) => {
    console.log('🔴 handleSubmit called - Creating live stream');
    
    e.preventDefault();
    e.stopPropagation();
    
    // Only proceed if on step 2 and we have a created room
    if (activeStep !== 2) {
      console.log('⚠️ Not on step 2, aborting');
      return;
    }
    
    if (!createdRoom) {
      console.log('❌ No room created yet');
      setErrors({ submit: 'Room must be created first' });
      return;
    }
    
 

    setIsLoading(true);
    
    try {
      // Create live stream for the existing room
      const streamData = {
        room_id: createdRoom.id,
        title: formData.title,  // Can use same title or different
        description: formData.description,
        strain: formData.strain,
        max_participants: formData.max_participants,
        max_viewers: formData.is_public ? formData.max_viewers : 0,
        allow_viewers_video: false,  // Can be another form field
        tags: formData.tags.join(','),
        auto_start: formData.auto_start_stream
      };
      
      console.log('🔴 Creating live stream:', streamData);
      
      // Call live stream API (you'll need to create this)
      const streamResponse = await createLiveStream(streamData);
      
      if (streamResponse && streamResponse.stream) {
        console.log('✅ Live stream created:', streamResponse.stream.id);
        
        // Show success and optionally auto-join
        if (formData.auto_start_stream) {
          console.log('🚀 Auto-joining room with live stream');
          // You can trigger auto-join here if needed
        }
      }
      
      // Reset form and close modal
      setFormData({
        title: '',
        description: '',
        category: '',
        strain: '',
        tags: [],
        is_public: true,
        max_participants: 8,
        max_viewers: 100,
        invite_only: false,
        auto_start_stream: true
      });
      setActiveStep(1);
      setCreatedRoom(null);
      onClose();
      
    } catch (error) {
      console.error('❌ Failed to create live stream:', error);
      setErrors({ submit: error.message || 'Failed to create live stream' });
    } finally {
      setIsLoading(false);
    }
  };
  const createLiveStream = async (streamData) => {
    const response = await fetch(`${API_URL}/api/rooms/${streamData.room_id}/streams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(streamData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create stream');
    }
    
    return await response.json();
  };

    const nextStep = async () => {
      console.log('🟢 nextStep called - Creating room first');
      
      if (activeStep === 1 && formData.title && formData.category) {
        try {
          setIsLoading(true);
          setErrors({}); // Clear any previous errors
          
          // Create room with basic info only
          const basicRoomData = {
            title: formData.title,
            description: formData.description,
            category: formData.category,
            is_public: formData.is_public,
            auto_start_stream: false  // Don't auto-start yet
          };
          
          console.log('🏠 Creating room with basic data:', basicRoomData);
          
          // Call your room creation API - MUST return response
          const response = await onCreateRoom(basicRoomData);
          console.log('📥 Response received from onCreateRoom:', response);
          
          if (response && response.room) {
            setCreatedRoom(response.room);
            console.log('✅ Room created successfully:', response.room.id);
            
            // Move to step 2 for stream settings
            setActiveStep(2);
            setStepChangeTime(Date.now());
            setErrors({}); // Clear any errors
          } else {
            console.error('❌ Invalid response structure:', response);
            setErrors({ step1: 'Invalid response from server' });
          }
          
        } catch (error) {
          console.error('❌ Failed to create room:', error);
          setErrors({ step1: error.message || 'Failed to create room' });
        } finally {
          setIsLoading(false);
        }
      } else {
        setErrors({ step1: 'Please fill in the title and select a category' });
      }
    };

  if (!isOpen) return null;

  const selectedCategory = seshCategories.find(cat => cat.id === formData.category);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(10px)',
      padding: '20px'
    }}>
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '24px',
        border: '2px solid #333',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        color: 'white',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)'
            }}>
              <Leaf size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
                Create Sesh Room
              </h2>
              <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
                Step {activeStep} of 2 - {activeStep === 1 ? 'Room Basics' : 'Settings & Launch'}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              padding: '12px',
              background: 'transparent',
              border: '1px solid #333',
              borderRadius: '12px',
              cursor: 'pointer',
              color: '#888',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#333';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#888';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{
          height: '4px',
          background: '#333',
          position: 'relative'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
            width: `${(activeStep / 2) * 100}%`,
            transition: 'width 0.3s ease'
          }} />
        </div>

        {/* Form Content */}
        <div style={{
          padding: '32px',
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 200px)'
        }}>
          {/* Error Messages */}
          {(errors.submit || errors.step1) && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              color: '#fca5a5',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertCircle size={20} />
              <span>{errors.submit || errors.step1}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && activeStep !== 2) {
              e.preventDefault();
              if (activeStep === 1 && formData.title && formData.category) {
                nextStep();
              }
            }
          }}>
            {activeStep === 1 && (
              <div>
                {/* Room Title */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    Sesh Room Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="e.g., Morning Wake & Bake with Blue Dream 🌅"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: `2px solid ${errors.title ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}`,
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                    onBlur={(e) => e.currentTarget.style.borderColor = errors.title ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}
                  />
                  {errors.title && (
                    <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
                      {errors.title}
                    </p>
                  )}
                  <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>
                    {formData.title.length}/80 characters
                  </p>
                </div>

                {/* Category Selection */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    Choose Your Sesh Category
                  </label>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px'
                  }}>
                    {seshCategories.map((category) => (
                      <div
                        key={category.id}
                        onClick={() => handleInputChange('category', category.id)}
                        style={{
                          padding: '16px',
                          background: formData.category === category.id 
                            ? `linear-gradient(135deg, ${category.color}20, ${category.color}10)` 
                            : 'rgba(255, 255, 255, 0.05)',
                          border: `2px solid ${formData.category === category.id ? category.color : 'rgba(255, 255, 255, 0.1)'}`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (formData.category !== category.id) {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.borderColor = category.color;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (formData.category !== category.id) {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          }
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '8px'
                        }}>
                          <span style={{ fontSize: '20px' }}>{category.icon}</span>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: formData.category === category.id ? category.color : '#e5e5e5'
                          }}>
                            {category.name}
                          </span>
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: '12px',
                          color: '#888',
                          lineHeight: '1.4'
                        }}>
                          {category.description}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {errors.category && (
                    <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '12px' }}>
                      {errors.category}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Tell people what to expect in your sesh room..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: `2px solid ${errors.description ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}`,
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                    onBlur={(e) => e.currentTarget.style.borderColor = errors.description ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}
                  />
                  <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>
                    {formData.description.length}/300 characters
                  </p>
                </div>
              </div>
            )}

            {activeStep === 2 && (
              <div>
                {/* Current Strain */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    What strain are you smoking? (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.strain}
                    onChange={(e) => handleInputChange('strain', e.target.value)}
                    placeholder="e.g., Blue Dream, OG Kush, or leave blank"
                    list="strain-suggestions"
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'}
                  />
                  <datalist id="strain-suggestions">
                    {popularStrains.map(strain => (
                      <option key={strain} value={strain} />
                    ))}
                  </datalist>
                </div>

                {/* Room Visibility */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    Room Visibility
                  </label>
                  
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div
                      onClick={() => handleInputChange('is_public', true)}
                      style={{
                        flex: 1,
                        padding: '20px',
                        background: formData.is_public 
                          ? 'rgba(16, 185, 129, 0.1)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        border: `2px solid ${formData.is_public ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textAlign: 'center'
                      }}
                    >
                      <Globe size={32} style={{ 
                        color: formData.is_public ? '#10b981' : '#888',
                        marginBottom: '12px'
                      }} />
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: formData.is_public ? '#10b981' : '#e5e5e5'
                      }}>
                        Public Room
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '12px',
                        color: '#888',
                        lineHeight: '1.4'
                      }}>
                        Anyone can join and chat. Viewers can watch the stream.
                      </p>
                    </div>

                    <div
                      onClick={() => {
                        handleInputChange('is_public', false);
                        handleInputChange('max_viewers', 0);
                      }}
                      style={{
                        flex: 1,
                        padding: '20px',
                        background: !formData.is_public 
                          ? 'rgba(139, 92, 246, 0.1)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        border: `2px solid ${!formData.is_public ? '#8b5cf6' : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textAlign: 'center'
                      }}
                    >
                      <Lock size={32} style={{ 
                        color: !formData.is_public ? '#8b5cf6' : '#888',
                        marginBottom: '12px'
                      }} />
                      <h3 style={{
                        margin: '0 0 8px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: !formData.is_public ? '#8b5cf6' : '#e5e5e5'
                      }}>
                        Private Room
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: '12px',
                        color: '#888',
                        lineHeight: '1.4'
                      }}>
                        Invite-only room. Only invited members can join.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Room Settings */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      Max Active Participants
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="20"
                      value={formData.max_participants}
                      onChange={(e) => handleInputChange('max_participants', parseInt(e.target.value) || 2)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <p style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
                      Can use video/audio
                    </p>
                  </div>

                  {formData.is_public && (
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#e5e5e5'
                      }}>
                        Max Viewers
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        value={formData.max_viewers}
                        onChange={(e) => handleInputChange('max_viewers', parseInt(e.target.value) || 0)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '2px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: 'white',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      <p style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
                        Can watch & chat only
                      </p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    Tags (Optional)
                  </label>
                  
                  {/* Current Tags */}
                  {formData.tags.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      {formData.tags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            padding: '6px 12px',
                            background: '#00d4aa',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          #{tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '14px',
                              padding: '0',
                              lineHeight: '1'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Add Custom Tag */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      value={currentTag}
                      onChange={(e) => setCurrentTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add custom tag..."
                      maxLength={20}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => addTag()}
                      disabled={!currentTag.trim() || formData.tags.length >= 8}
                      style={{
                        padding: '10px 16px',
                        background: currentTag.trim() && formData.tags.length < 8 ? '#00d4aa' : '#333',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: currentTag.trim() && formData.tags.length < 8 ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {/* Popular Tags */}
                  <div>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      Popular tags:
                    </p>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {communityTags.filter(tag => !formData.tags.includes(tag)).slice(0, 12).map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => addTag(tag)}
                          disabled={formData.tags.length >= 8}
                          style={{
                            padding: '4px 10px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '12px',
                            color: '#888',
                            fontSize: '11px',
                            cursor: formData.tags.length < 8 ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (formData.tags.length < 8) {
                              e.currentTarget.style.background = 'rgba(0, 212, 170, 0.2)';
                              e.currentTarget.style.color = '#00d4aa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.color = '#888';
                          }}
                        >
                          #{tag}
                        </button>
                      ))}
                    </div>
                    <p style={{
                      margin: '8px 0 0 0',
                      fontSize: '11px',
                      color: '#666'
                    }}>
                      {formData.tags.length}/8 tags
                    </p>
                  </div>
                </div>

                {/* Auto-start Stream Option */}
                <div style={{
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#e5e5e5'
                      }}>
                        Auto-start stream when room is created
                      </h4>
                      <p style={{
                        margin: 0,
                        fontSize: '12px',
                        color: '#888'
                      }}>
                        Your room will go live immediately after creation
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleInputChange('auto_start_stream', !formData.auto_start_stream)}
                      style={{
                        width: '48px',
                        height: '24px',
                        background: formData.auto_start_stream ? '#00d4aa' : '#666',
                        border: 'none',
                        borderRadius: '12px',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: formData.auto_start_stream ? '26px' : '2px',
                        transition: 'all 0.3s ease'
                      }} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation & Submit */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '24px',
              borderTop: '1px solid #333'
            }}>
                {activeStep === 1 ? (
                  <>
                    <div />
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={isLoading || !formData.title || !formData.category}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        background: (formData.title && formData.category && !isLoading) ? 
                          'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' : '#666',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: (formData.title && formData.category && !isLoading) ? 'pointer' : 'not-allowed',
                        fontSize: '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {isLoading ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            borderTop: '2px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Creating Room...
                        </>
                      ) : (
                        <>
                          Continue
                          <ChevronRight size={20} />
                        </>
                      )}
                    </button>
                  </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setActiveStep(1)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      background: 'transparent',
                      color: '#888',
                      border: '1px solid #333',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#333';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#888';
                    }}
                  >
                    <ChevronLeft size={16} />
                    Back
                  </button>

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px 32px',
                      background: isLoading ? '#666' : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '16px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      boxShadow: isLoading ? 'none' : '0 8px 20px rgba(16, 185, 129, 0.4)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isLoading ? 'none' : '0 8px 20px rgba(16, 185, 129, 0.4)';
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Creating Room...
                      </>
                    ) : (
                      <>
                        <Video size={20} />
                        {formData.auto_start_stream ? 'Create & Go Live' : 'Create Room'}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </form>

          {/* Preview Card */}
          {activeStep === 2 && selectedCategory && (
            <div style={{
              marginTop: '32px',
              padding: '20px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Room Preview
              </h4>
              
              <div style={{
                background: colors.bgTertiary,
                border: '1px solid #333',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>{selectedCategory.icon}</span>
                  <h3 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    {formData.title || 'Your Sesh Room Title'}
                  </h3>
                  {!formData.is_public && <Lock size={14} style={{ color: '#8b5cf6' }} />}
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  fontSize: '12px',
                  color: '#888'
                }}>
                  <span style={{ color: selectedCategory.color }}>
                    {selectedCategory.name}
                  </span>
                  {formData.strain && (
                    <>
                      <span>•</span>
                      <span>🌿 {formData.strain}</span>
                    </>
                  )}
                </div>

                {formData.tags.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    marginBottom: '12px'
                  }}>
                    {formData.tags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          padding: '2px 6px',
                          background: '#333',
                          borderRadius: '4px',
                          fontSize: '10px',
                          color: '#888'
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '12px',
                  color: '#888'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Users size={12} />
                    Max {formData.max_participants} participants
                  </span>
                  {formData.is_public && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Eye size={12} />
                      Max {formData.max_viewers} viewers
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CreateSeshRoomModal;